import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminDashboard from './components/admin/AdminDashboard';
import Login from './components/Login';
import Register from './components/Register';
import PredictionForm from './components/PredictionForm';
import Scoreboard from './components/Scoreboard';
import PaymentUpload from './components/PaymentUpload';
import Home from './components/Home';
import Navbar from './components/Navbar';
import Profile from './components/Profile';
import NotFound from './components/NotFound';
import Footer from './components/Footer';
import './styles/index.css';

// Admin Protected Route
// Restricts access to admin-only content
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div role="status" aria-live="polite">Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!user.is_admin) {
    return <div role="alert">Acesso negado</div>;
  }

  return children;
};

// Protected Route for logged in users
// Restricts access to authenticated users
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div role="status" aria-live="polite">Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Guest Route for non-logged in users
// Restricts access to unauthenticated users (like login/register)
const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div role="status" aria-live="polite">Carregando...</div>;
  }

  if (user) {
    return <Navigate to="/" />;
  }

  return children;
};

// Main Application Component
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-white flex flex-col">
          <Navbar />

          <main className="container mx-auto px-4 py-8 flex-grow">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route 
                path="/login" 
                element={
                  <GuestRoute>
                    <Login />
                  </GuestRoute>
                } 
              />
              <Route 
                path="/register" 
                element={
                  <GuestRoute>
                    <Register />
                  </GuestRoute>
                } 
              />

              {/* Protected Routes */}
              <Route
                path="/predictions"
                element={
                  <ProtectedRoute>
                    <PredictionForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scoreboard"
                element={
                  <ProtectedRoute>
                    <Scoreboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment"
                element={
                  <ProtectedRoute>
                    <PaymentUpload />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />

              {/* 404 Route */}
              <Route
                path="*"
                element={<NotFound />}
              />
            </Routes>
          </main>

          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;